/**
 * Tenant fiscal year and tax label, derived at read time.
 *
 * Stored on the lowest-id farm as `locale.fiscalYear` / `locale.tax`. A rule never stores its
 * window or its label; those are computed here. Periods mode is an editable list the tenant typed.
 * Pure: no React, no fetch, no clock inside a window lookup (`now` is an argument).
 */

import i18next from 'i18next'

const pad = n => String(n).padStart(2, '0')

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

export const DEFAULT_FISCAL_YEAR = { mode: 'rule', rule: { startMonth: 7, namedBy: 'endingYear' }, periods: [] }
export const DEFAULT_TAX = { label: 'GST' }

const toIsoDate = value => {
    if (value == null || value === '') return null
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10)
    const ms = typeof value === 'number' ? (value > 1e11 ? value : value * 1000) : Date.parse(value)
    if (Number.isNaN(ms)) return null
    return new Date(ms).toISOString().slice(0, 10)
}

export const addDays = (iso, days) => {
    const date = toIsoDate(iso)
    if (!date) return null
    const [year, month, day] = date.split('-').map(Number)
    const next = new Date(Date.UTC(year, month - 1, day + days))
    return `${next.getUTCFullYear()}-${pad(next.getUTCMonth() + 1)}-${pad(next.getUTCDate())}`
}

export const formatIsoDay = iso => {
    const date = toIsoDate(iso)
    if (!date) return ''
    const [year, month, day] = date.split('-').map(Number)
    return `${day} ${MONTHS[month - 1]} ${year}`
}

/** `FY2027 · 1 Jul 2026 - 30 Jun 2027` */
export const formatYearLine = (year = {}) => {
    if (!year?.from || !year?.to) return year?.label || ''
    const label = year.label || `FY${String(year.to).slice(0, 4)}`
    return `${label} · ${formatIsoDay(year.from)} - ${formatIsoDay(year.to)}`
}

export const normaliseFiscalYear = setting => {
    const mode = setting?.mode === 'periods' ? 'periods' : 'rule'
    const startMonth = Number(setting?.rule?.startMonth)
    const namedBy = setting?.rule?.namedBy === 'startingYear' ? 'startingYear' : 'endingYear'
    const periods = Array.isArray(setting?.periods) ? setting.periods.filter(period => period && (period.from || period.to || period.label)) : []
    return {
        mode,
        rule: {
            startMonth: startMonth >= 1 && startMonth <= 12 ? startMonth : 7,
            namedBy,
        },
        periods,
    }
}

export const fiscalYearFromFarm = farm => (farm?.locale?.fiscalYear ? normaliseFiscalYear(farm.locale.fiscalYear) : { ...DEFAULT_FISCAL_YEAR, rule: { ...DEFAULT_FISCAL_YEAR.rule }, periods: [] })

/** Lowest-id farm that carries `locale.fiscalYear`, else the Australian default. */
export const fiscalYearFromFarms = farms => {
    const list = [...(farms || [])].filter(farm => farm && farm.id != null).sort((a, b) => Number(a.id) - Number(b.id))
    const carrying = list.find(farm => farm?.locale?.fiscalYear)
    return fiscalYearFromFarm(carrying)
}

export const taxLabelFromFarms = farms => {
    const list = [...(farms || [])].filter(farm => farm && farm.id != null).sort((a, b) => Number(a.id) - Number(b.id))
    const carrying = list.find(farm => farm?.locale?.tax && Object.prototype.hasOwnProperty.call(farm.locale.tax, 'label'))
    if (!carrying) return DEFAULT_TAX.label
    const label = carrying.locale.tax.label
    return label == null ? '' : String(label)
}

export const yearWindowForRule = (rule, year) => {
    const startMonth = Number(rule?.startMonth) >= 1 && Number(rule?.startMonth) <= 12 ? Number(rule.startMonth) : 7
    const namedBy = rule?.namedBy === 'startingYear' ? 'startingYear' : 'endingYear'
    const y = Number(year)
    if (!Number.isFinite(y)) throw new Error('financialYear: a year is required')
    const startYear = namedBy === 'startingYear' || startMonth === 1 ? y : y - 1
    const endYear = startMonth === 1 ? startYear : startYear + 1
    const from = `${startYear}-${pad(startMonth)}-01`
    const endMonth = startMonth === 1 ? 12 : startMonth - 1
    const lastDay = new Date(Date.UTC(endYear, endMonth, 0)).getUTCDate()
    const to = `${endYear}-${pad(endMonth)}-${pad(lastDay)}`
    const labelYear = namedBy === 'startingYear' ? startYear : endYear
    return { from, to, label: `FY${labelYear}` }
}

export const currentYearKey = (rule, date) => {
    const iso = toIsoDate(date)
    if (!iso) return null
    const y = Number(iso.slice(0, 4))
    const m = Number(iso.slice(5, 7))
    const startMonth = Number(rule?.startMonth) >= 1 && Number(rule?.startMonth) <= 12 ? Number(rule.startMonth) : 7
    const namedBy = rule?.namedBy === 'startingYear' ? 'startingYear' : 'endingYear'
    if (startMonth === 1) return y
    const inStartYear = m >= startMonth
    if (namedBy === 'startingYear') return inStartYear ? y : y - 1
    return inStartYear ? y + 1 : y
}

export const yearWindow = (setting, year) => {
    const s = normaliseFiscalYear(setting)
    if (s.mode === 'periods' && s.periods.length) {
        const wanted = `FY${year}`
        const hit = s.periods.find(period => period.label === wanted || period.label === String(year)) || s.periods.find(period => period.from && Number(period.from.slice(0, 4)) === Number(year))
        if (hit?.from && hit?.to) return { from: hit.from, to: hit.to, label: hit.label || wanted }
    }
    return yearWindowForRule(s.rule, year)
}

export const currentYear = (setting, date) => {
    const s = normaliseFiscalYear(setting)
    const iso = toIsoDate(date)
    if (s.mode === 'periods' && s.periods.length && iso) {
        const hit = s.periods.find(period => period.from && period.to && iso >= period.from && iso <= period.to)
        if (hit) return { from: hit.from, to: hit.to, label: hit.label || `FY${hit.to.slice(0, 4)}` }
    }
    const key = currentYearKey(s.rule, date)
    return key == null ? null : yearWindowForRule(s.rule, key)
}

export const selectableYears = (setting, date, count = 5) => {
    const s = normaliseFiscalYear(setting)
    if (s.mode === 'periods' && s.periods.length) {
        return [...s.periods]
            .filter(period => period.from && period.to)
            .sort((a, b) => b.from.localeCompare(a.from))
            .map(period => ({ from: period.from, to: period.to, label: period.label || `FY${period.to.slice(0, 4)}` }))
    }
    const key = currentYearKey(s.rule, date)
    if (key == null) return []
    return Array.from({ length: count }, (_, i) => yearWindowForRule(s.rule, key - i))
}

export const proposeNextPeriod = (setting, date) => {
    const s = normaliseFiscalYear(setting)
    const sorted = [...s.periods].filter(period => period.from && period.to).sort((a, b) => a.from.localeCompare(b.from))
    if (!sorted.length) {
        const current = currentYear({ ...s, mode: 'rule' }, date)
        return current ? { label: current.label, from: current.from, to: current.to } : null
    }
    const nextFrom = addDays(sorted[sorted.length - 1].to, 1)
    const [y, m, d] = nextFrom.split('-').map(Number)
    const to = addDays(`${y + 1}-${pad(m)}-${pad(d)}`, -1)
    const labelYear = s.rule.namedBy === 'startingYear' ? y : Number(to.slice(0, 4))
    return { label: `FY${labelYear}`, from: nextFrom, to }
}

/**
 * Consecutive periods must meet: each `from` is the day after the previous `to`.
 * Names the first orphaned or doubled day. Null when the list is sound (including a single period).
 */
export const validatePeriods = periods => {
    const list = (periods || []).filter(period => period && (period.from || period.to))
    for (const period of list) {
        if (!period.from || !period.to) return i18next.t('11013')
        if (period.from > period.to) return i18next.t('11014', { from: formatIsoDay(period.from), to: formatIsoDay(period.to) })
    }
    const sorted = [...list].sort((a, b) => a.from.localeCompare(b.from))
    for (let i = 1; i < sorted.length; i += 1) {
        const expectedFrom = addDays(sorted[i - 1].to, 1)
        const nextFrom = sorted[i].from
        if (nextFrom > expectedFrom) return i18next.t('11015', { date: formatIsoDay(expectedFrom) })
        if (nextFrom < expectedFrom) return i18next.t('11016', { date: formatIsoDay(nextFrom) })
    }
    return null
}

export default {
    DEFAULT_FISCAL_YEAR,
    DEFAULT_TAX,
    fiscalYearFromFarms,
    taxLabelFromFarms,
    yearWindow,
    currentYear,
    selectableYears,
    formatYearLine,
    validatePeriods,
    proposeNextPeriod,
}
